import { executeCommand } from '../Utils/SendCommandToACC';
import { isNetworkError } from '../Utils/accClient';
import { getUserName } from '../Utils/GeneralUtils';
import { isMockMode } from './mockMode';

/**
 * Knowledge Base API client.
 *
 * The ACC Lambda returns its body as a string; our knowledge commands return
 * JSON of the shape { success: boolean, data?: any, error?: string }. This
 * module parses that envelope and throws on failure so callers can use
 * try/catch like the rest of the app.
 *
 * When mock mode is enabled (see mockMode.js) commands are served by an
 * in-memory store instead of the ACC Lambda, so the UI runs fully offline. The
 * store is loaded on demand: this module sits in the main chunk (Header ->
 * useAdminOnlyPermissions) and the seeded articles must not ship with it.
 */

const dispatch = (command, args = []) =>
    isMockMode()
        ? import('./mockKnowledgeData').then((m) => m.mockExecuteCommand(command, args))
        : executeCommand(command, args);

const parseEnvelope = (raw) => {
    if (raw && typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const run = async (command, args = []) => {
    const raw = await dispatch(command, args);
    const envelope = parseEnvelope(raw);
    if (!envelope) {
        // A non-JSON body usually means a 403 permission string or an error.
        throw new Error(typeof raw === 'string' ? raw : 'Unexpected response from server.');
    }
    if (!envelope.success) {
        throw new Error(envelope.error || 'Request failed.');
    }
    return envelope.data;
};

// --- Reads / acknowledgments (all roles) ---
export const getArticles = () => run('get_knowledge_articles', []);
export const getArticle = (articleId) => run('get_knowledge_article', [articleId]);
export const searchKnowledge = (term) => run('search_knowledge', [term]);
export const getFolders = () => run('get_knowledge_folders', []);
export const getMyKnowledge = () => run('get_my_knowledge', []);
export const acknowledgeArticle = (articleId) => run('acknowledge_knowledge_article', [articleId]);
export const flagArticle = (articleId, note = '') => run('flag_knowledge_article', [articleId, note]);

// --- Authoring / management (editor roles only; backend 403s otherwise) ---
export const addArticle = (payload) => run('add_knowledge_article', [payload]);
export const updateArticle = (articleId, payload) => run('update_knowledge_article', [articleId, payload]);
export const deleteArticle = (articleId) => run('delete_knowledge_article', [articleId]);
export const addFolder = (name, parentId = null) => run('add_knowledge_folder', [name, parentId]);
export const deleteFolder = (folderId) => run('delete_knowledge_folder', [folderId]);
export const assignArticle = (articleId, assignee, dueDate = '') =>
    run('assign_knowledge_article', [articleId, assignee, dueDate]);
export const unassignArticle = (articleId, assignee) =>
    run('unassign_knowledge_article', [articleId, assignee]);
export const getArticleAcknowledgments = (articleId) =>
    run('get_knowledge_article_acknowledgments', [articleId]);

/**
 * `{ articleId, steps, current }` — what the server's parser would derive from
 * the saved body next to the steps already stored, so the editor can offer
 * "Generate steps from body" without writing anything.
 */
export const previewArticleSteps = (articleId) => run('preview_article_steps', [articleId]);

/**
 * `get_allowed_commands` returns a Python list rendered as a string (e.g.
 * "['a', 'b']"), so we parse leniently rather than assuming valid JSON.
 */
const parseAllowedCommands = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch (e) {
            const matches = raw.match(/'([^']+)'|"([^"]+)"/g) || [];
            return matches.map((m) => m.slice(1, -1));
        }
    }
    return [];
};

/**
 * The caller's allowed commands, which gate the edit and admin UI. One read
 * per signed-in operator for the session, shared by every permission hook
 * (Manager Admin alone mounted three of them). The in-flight promise is what
 * is cached, so StrictMode's double mount joins it. A network error is
 * forgotten so the next mount retries; a refusal is kept, since the answer
 * cannot change mid-session.
 */
const allowedByUser = new Map();

export const getAllowedCommands = () => {
    const user = getUserName() || '';
    if (!allowedByUser.has(user)) {
        allowedByUser.set(
            user,
            dispatch('get_allowed_commands', []).then(parseAllowedCommands).catch((e) => {
                if (isNetworkError(e)) allowedByUser.delete(user);
                throw e;
            })
        );
    }
    return allowedByUser.get(user);
};

/** Test helper, and for a sign-in as someone else on the same tab: forget every cached answer. */
export const resetAllowedCommandsCache = () => allowedByUser.clear();

export const WHEN_NEEDED_OPTIONS = [
    { value: '', label: 'Uncategorized' },
    { value: 'before-shift', label: 'Before shift' },
    { value: 'during-shift', label: 'During shift' },
    { value: 'when-x-breaks', label: 'When something breaks' },
    { value: 'reference', label: 'Reference' },
];

export const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};
